/**
 * Tiny dependency-free Markdown → HTML renderer for the limited subset our LLMs return.
 * Supports: H2/H3 headings, paragraphs, **bold**, *italic*, `code`, bullet & numbered lists,
 * blockquotes, hr, line breaks. Output is sanitized by escaping HTML first.
 */
export function mdToHtml(md) {
  if (!md) return "";
  // Escape HTML
  let s = String(md)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");

  // Code spans
  s = s.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-[#EAE4D5] text-[#0A0A0A] text-[0.85em] font-mono-aq">$1</code>');
  // Bold
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Italic (only single * not part of ** which is already handled)
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');

  const lines = s.split(/\r?\n/);
  const out = [];
  let inUl = false, inOl = false, inBq = false;
  const closeLists = () => {
    if (inUl) { out.push("</ul>"); inUl = false; }
    if (inOl) { out.push("</ol>"); inOl = false; }
    if (inBq) { out.push("</blockquote>"); inBq = false; }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { closeLists(); continue; }
    if (/^---+$/.test(line)) { closeLists(); out.push('<hr class="my-6 border-[#B6B09F]/40" />'); continue; }
    let m;
    if ((m = line.match(/^###\s+(.*)$/))) { closeLists(); out.push(`<h3 class="font-serif-display text-xl mt-6 mb-2">${m[1]}</h3>`); continue; }
    if ((m = line.match(/^##\s+(.*)$/))) { closeLists(); out.push(`<h2 class="font-serif-display text-2xl md:text-3xl mt-8 mb-3">${m[1]}</h2>`); continue; }
    if ((m = line.match(/^#\s+(.*)$/))) { closeLists(); out.push(`<h2 class="font-serif-display text-3xl mt-8 mb-3">${m[1]}</h2>`); continue; }
    if ((m = line.match(/^[-*]\s+(.*)$/))) {
      if (!inUl) { closeLists(); out.push('<ul class="list-disc pl-6 space-y-1 my-2">'); inUl = true; }
      out.push(`<li>${m[1]}</li>`); continue;
    }
    if ((m = line.match(/^\d+\.\s+(.*)$/))) {
      if (!inOl) { closeLists(); out.push('<ol class="list-decimal pl-6 space-y-1 my-2">'); inOl = true; }
      out.push(`<li>${m[1]}</li>`); continue;
    }
    if ((m = line.match(/^>\s?(.*)$/))) {
      if (!inBq) { closeLists(); out.push('<blockquote class="border-l-2 border-[#B6B09F] pl-4 my-3 text-[#4A4A4A]">'); inBq = true; }
      out.push(`<p>${m[1]}</p>`); continue;
    }
    closeLists();
    out.push(`<p class="leading-relaxed my-2">${line}</p>`);
  }
  closeLists();
  return out.join("\n");
}

export default function Markdown({ children, className = "" }) {
  return (
    <div
      className={`prose-aqion ${className}`}
      dangerouslySetInnerHTML={{ __html: mdToHtml(children) }}
    />
  );
}
